/**
 * Route gate for ClinicalHUD3 Live mode:
 * - unsigned → auth with return path
 * - global admin → children + one fire-and-forget admin-bypass log
 * - grant + attestation → children
 * - grant, no attestation → attestation screen
 * - no grant → owner-issued grant required message
 *
 */
import { useEffect, useRef } from "react";
import { Link as RouterLink, Navigate, useLocation } from "react-router-dom";
import { Alert, Box, Link, Typography } from "@mui/material";
import Loading from "components/Loading";
import { useAuth } from "hooks/useAuth";
import { useClinicalHud3LiveAccess } from "../useClinicalHud3LiveAccess";
import { logClinicalHud3AdminLiveEntry } from "../clinicalHud3LiveCallables";
import { CLINICAL_HUD3_TEACHING_PATH } from "../clinicalHud3Paths";
import ClinicalHud3LiveAttestationScreen from "./ClinicalHud3LiveAttestationScreen";

export default function RequireClinicalHud3LiveAccess({ children }) {
  const { user } = useAuth();
  const {
    loading,
    isAdmin,
    canEnterLive,
    needsAttestation,
  } = useClinicalHud3LiveAccess();
  const location = useLocation();
  const loggedAdminEntry = useRef(false);

  useEffect(() => {
    if (!isAdmin || loggedAdminEntry.current) return undefined;
    loggedAdminEntry.current = true;
    Promise.resolve(logClinicalHud3AdminLiveEntry()).catch((err) => {
      console.error(
        "[RequireClinicalHud3LiveAccess] admin bypass log failed",
        err,
      );
    });
    return undefined;
  }, [isAdmin]);

  if (loading) return <Loading page />;

  if (!user) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth?next=${encodeURIComponent(next)}`} replace />;
  }

  if (isAdmin || canEnterLive) {
    return children;
  }

  if (needsAttestation) {
    return <ClinicalHud3LiveAttestationScreen />;
  }

  return (
    <Box sx={{ maxWidth: 560, mx: "auto", py: 6, px: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        ClinicalHUD3 Live
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Live mode requires an owner-issued grant.
      </Alert>
      <Link component={RouterLink} to={CLINICAL_HUD3_TEACHING_PATH}>
        Back to Teaching
      </Link>
    </Box>
  );
}
